import { Users, Server, Briefcase, Shield, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import {
  useGetDashboardStatsQuery,
  useGetRecentActivityQuery,
  useGetServicesQuery,
} from '@/store/api/apiSlice';
import { formatDistanceToNow } from 'date-fns';
import type { AuditEvent, ServiceSubject, DashboardStats } from '@/types';

export function DashboardPage() {
  const { data: statsRaw, isLoading: statsLoading } = useGetDashboardStatsQuery(undefined);
  const { data: activity, isLoading: activityLoading } = useGetRecentActivityQuery({ limit: 10 });
  const { data: services, isLoading: servicesLoading } = useGetServicesQuery(undefined);

  const stats = statsRaw as DashboardStats | undefined;

  const statCards = [
    { title: 'Active Users', value: stats?.activeUsers ?? 0, icon: Users, color: 'text-blue-600' },
    { title: 'Services Running', value: stats?.servicesRunning ?? 0, icon: Server, color: 'text-green-600' },
    { title: 'Jobs in Queue', value: stats?.jobsInQueue ?? 0, icon: Briefcase, color: 'text-amber-600' },
    { title: 'Policy Evaluations', value: stats?.policyEvaluations ?? 0, icon: Shield, color: 'text-purple-600' },
  ];

  return (
    <div className="space-y-6">
      {/* Stats cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {card.title}
              </CardTitle>
              <card.icon className={`h-5 w-5 ${card.color}`} />
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <p className="text-2xl font-bold">{card.value.toLocaleString()}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Service health grid */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Service Health</CardTitle>
          </CardHeader>
          <CardContent>
            {servicesLoading ? (
              <LoadingSpinner className="py-8" />
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {(services as ServiceSubject[] | undefined)?.map((svc) => (
                  <div
                    key={svc._id}
                    className="flex flex-col items-center gap-2 rounded-lg border p-3"
                  >
                    <Server className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm font-medium">{svc.serviceName}</span>
                    <StatusBadge status={svc.status} />
                  </div>
                )) ?? (
                  <p className="col-span-full text-center text-sm text-muted-foreground py-4">
                    No services registered
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent activity feed */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {activityLoading ? (
              <LoadingSpinner className="py-8" />
            ) : (
              <div className="space-y-3">
                {(activity as AuditEvent[] | undefined)?.map((event) => (
                  <div key={event._id} className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-full bg-muted p-1.5">
                      <Activity className="h-3 w-3 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        <span className="font-semibold">{event.actor}</span>{' '}
                        {event.action}{' '}
                        <span className="text-muted-foreground">{event.target}</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                )) ?? (
                  <p className="text-center text-sm text-muted-foreground py-4">
                    No recent activity
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
